/**
 * Order Storage Utility
 * Manages persistent storage of guest table orders in localStorage
 */

export interface StoredOrder {
  // Order details from API
  id: string;
  order_number: string;
  status: string;
  total_amount: string;
  items: any[];
  restaurant_name: string;
  table_number: string;
  created_at: string;
  
  // Guest info for tracking
  guest_name: string;
  guest_phone: string;
  order_type: string;
  
  // Local metadata
  timestamp: number;
  table_context: {
    hotelId: string;
    restaurantId: string;
    tableId: string;
  };
}

const ORDER_STORAGE_KEY = 't_orders';
const MAX_STORAGE_DAYS = 1; // Keep orders for 1 day

/**
 * Save a new order to localStorage
 */
export function saveOrder(orderData: any, guestInfo: any, tableContext: any): StoredOrder {
  const storedOrder: StoredOrder = {
    // API data
    id: orderData.id,
    order_number: orderData.order_number,
    status: orderData.status,
    total_amount: orderData.total_amount,
    items: orderData.items || [],
    restaurant_name: orderData.restaurant_name,
    table_number: orderData.table_number,
    created_at: orderData.created_at,
    
    // Guest info
    guest_name: guestInfo.name,
    guest_phone: guestInfo.phone,
    order_type: orderData.type || 'table_service',
    
    // Local metadata
    timestamp: Date.now(),
    table_context: {
      hotelId: tableContext.hotelId,
      restaurantId: tableContext.restaurantId,
      tableId: tableContext.tableId
    }
  };

  const existingOrders = getOrders();
  const updatedOrders = [storedOrder, ...existingOrders];
  
  // Clean up old orders while saving
  const cleanedOrders = cleanOldOrders(updatedOrders);
  
  localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(cleanedOrders));
  console.log('[OrderStorage] Saved order:', storedOrder.order_number);
  
  return storedOrder;
}

/**
 * Get all stored orders (sorted by date, newest first)
 */
export function getOrders(): StoredOrder[] {
  try {
    const stored = localStorage.getItem(ORDER_STORAGE_KEY);
    if (!stored) return [];
    
    const orders: StoredOrder[] = JSON.parse(stored);
    return orders.sort((a, b) => b.timestamp - a.timestamp);
  } catch (error) {
    console.error('[OrderStorage] Error reading orders:', error);
    return [];
  }
}

/**
 * Get orders with active statuses only
 */
export function getActiveOrders(): StoredOrder[] {
  const activeStatuses = ['pending', 'confirmed', 'preparing', 'ready', 'served'];
  return getOrders().filter(order => activeStatuses.includes(order.status));
}

/**
 * Find a specific order by order number
 */
export function getOrderByNumber(orderNumber: string): StoredOrder | null {
  const orders = getOrders();
  return orders.find(order => order.order_number === orderNumber) || null;
}

/**
 * Update the status of a cached order
 */
export function updateOrderStatus(orderNumber: string, newStatus: string, updatedOrderData?: any): boolean {
  const orders = getOrders();
  const orderIndex = orders.findIndex(order => order.order_number === orderNumber);
  
  if (orderIndex === -1) {
    console.warn('[OrderStorage] Order not found for status update:', orderNumber);
    return false;
  }
  
  // Update status and any other provided data
  orders[orderIndex] = {
    ...orders[orderIndex],
    status: newStatus,
    ...(updatedOrderData || {})
  };
  
  localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(orders));
  console.log('[OrderStorage] Updated order status:', orderNumber, newStatus);
  
  return true;
}

/**
 * Get count of active orders
 */
export function getActiveOrderCount(): number {
  return getActiveOrders().length;
}

/**
 * Get the most recent order
 */
export function getLatestOrder(): StoredOrder | null {
  const orders = getOrders();
  return orders.length > 0 ? orders[0] : null;
}

/**
 * Remove orders older than specified days
 */
function cleanOldOrders(orders: StoredOrder[]): StoredOrder[] {
  const cutoffTime = Date.now() - (MAX_STORAGE_DAYS * 24 * 60 * 60 * 1000);
  const cleaned = orders.filter(order => order.timestamp > cutoffTime);
  
  if (cleaned.length !== orders.length) {
    console.log('[OrderStorage] Cleaned', orders.length - cleaned.length, 'old orders');
  }
  
  return cleaned;
}

/**
 * Clear all stored orders (for testing/debugging)
 */
export function clearAllOrders(): void {
  localStorage.removeItem(ORDER_STORAGE_KEY);
  console.log('[OrderStorage] Cleared all orders');
}

/**
 * Get orders for a specific table context
 */
export function getOrdersForTable(hotelId: string, restaurantId: string, tableId: string): StoredOrder[] {
  return getOrders().filter(order => 
    order.table_context.hotelId === hotelId &&
    order.table_context.restaurantId === restaurantId &&
    order.table_context.tableId === tableId
  );
}

/**
 * Get guest info from the most recent order (for reuse)
 */
export function getGuestInfoFromLatestOrder(): { name: string; phone: string } | null {
  const latest = getLatestOrder();
  if (!latest) return null;
  
  return {
    name: latest.guest_name,
    phone: latest.guest_phone
  };
}

/**
 * Check if we have any orders from the same guest phone
 */
export function hasOrdersForGuest(guestPhone: string): boolean {
  return getOrders().some(order => order.guest_phone === guestPhone);
}

/**
 * Export orders data for debugging
 */
export function exportOrdersData(): string {
  const orders = getOrders();
  return JSON.stringify(orders, null, 2);
}

// Utility function to migrate from old sessionStorage if needed
export function migrateFromSessionStorage(): boolean {
  try {
    const oldOrder = sessionStorage.getItem('t_last_order');
    if (oldOrder && getOrders().length === 0) {
      const orderData = JSON.parse(oldOrder);
      console.log('[OrderStorage] Migrating order from sessionStorage');
      
      // Create a stored order with minimal data
      const migratedOrder: StoredOrder = {
        id: orderData.id || 'unknown',
        order_number: orderData.order_number,
        status: orderData.status || 'pending',
        total_amount: orderData.total_amount || '0',
        items: orderData.items || [],
        restaurant_name: orderData.restaurant_name || 'Unknown Restaurant',
        table_number: orderData.table_number || 'Unknown',
        created_at: orderData.created_at || new Date().toISOString(),
        guest_name: 'Guest',
        guest_phone: 'Unknown',
        order_type: orderData.type || 'table_service',
        timestamp: Date.now(),
        table_context: {
          hotelId: 'unknown',
          restaurantId: 'unknown',
          tableId: 'unknown'
        }
      };
      
      localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify([migratedOrder]));
      sessionStorage.removeItem('t_last_order');
      
      return true;
    }
  } catch (error) {
    console.error('[OrderStorage] Migration error:', error);
  }
  
  return false;
}